import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import PageHeader from '../components/PageHeader'
import webinarImage from '../assets/webinar-nov-2025.jpg'
import { 
  ArrowTopRightOnSquareIcon, 
  MegaphoneIcon, 
  UserGroupIcon, 
  VideoCameraIcon,
  CalendarDaysIcon,
  ClockIcon,
  CheckCircleIcon,
  GlobeAltIcon,
  MapPinIcon,
  ArrowDownTrayIcon,
  PhotoIcon,
  SparklesIcon
} from '@heroicons/react/24/outline'

const newsItems = [
  {
    id: 1,
    source: 'The Guardian Newspaper',
    title: 'How Hospital Linked Remote Enugu Communities to Quality Healthcare',
    excerpt: 'Comprehensive report on how Goodwill Medical Centre and the Avah Bridge transformed health equity for underserved urban settlements in Enugu State.',
    date: 'April 2025',
    link: 'https://guardian.ng/news/how-hospital-linked-enugu-communities-to-healthcare/',
  },
  {
    id: 2,
    source: 'Punch Newspaper (Healthwise)',
    title: 'Hospital, Foundation Bring Life-Saving Care to Forgotten Enugu Communities',
    excerpt: 'Feature story showcasing the direct medical interventions, subsidized surgery, and maternal services provided to vulnerable households.',
    date: 'April 2025',
    link: 'https://healthwise.punchng.com/hospital-foundation-bring-life-saving-care-to-forgotten-enugu-communities/',
  },
  {
    id: 3,
    source: 'Nigeria Health Online',
    title: 'Relief as Enugu Deprived Communities Link Up to Healthcare',
    excerpt: 'Documenting the elimination of geographic barriers and the training of Traditional Birth Attendants to prevent maternal deaths.',
    date: 'April 2025',
    link: 'https://www.nigeriahealthonline.com/2025/04/10/relief-as-enugu-deprived-communities-link-up-healthcare.nho/?amp=1',
  },
  {
    id: 4,
    source: 'Lagos City Reporters',
    title: 'Joy and Relief as GMC Links Poor Communities to Essential Services',
    excerpt: 'Analysis of community sentiment following the completion of the Avah Bridge and commencement of regular eye and pediatric clinics.',
    date: 'April 2025',
    link: 'https://lagoscityreporters.com/joy-relief-as-gmc-links-poor-communities-to-essential-services-by-sam-eferaro/',
  },
  {
    id: 5,
    source: 'PGlobal Media',
    title: 'Transformative Healthcare Delivery in Underserved Enugu Slums',
    excerpt: 'How multi-sectoral collaboration between PACIEH, TY Danjuma Foundation, and local elders creates sustainable health infrastructure.',
    date: 'April 2025',
    link: 'https://pglobalmedia.com/2025/04/10/joy-relief-as-gmc-links-poor-communities-to-essential-services/',
  },
  {
    id: 6,
    source: 'Elendu Reports Online',
    title: 'Goodwill Medical Centre: Transforming Community Service',
    excerpt: 'Spotlight on founder Prof. Uche Amazigo and CEO Prof. Angela Ekwunife driving high-impact grassroots health innovations.',
    date: 'April 2025',
    link: 'https://www.elendureportsonline.com/goodwill-medical-centre-transforming-community-service/',
  },
]

export default function Media() {
  return (
    <>
      <Helmet>
        <title>Media, News & Events | PACIEH</title>
        <meta 
          name="description" 
          content="Explore national press coverage, leadership announcements, and global webinars featuring PACIEH and Goodwill Medical Centre." 
        />
      </Helmet>

      <PageHeader
        title="Media, News & Events"
        subtitle="National press coverage, governance milestones, and upcoming global health webinars documenting PACIEH's grassroots impact."
      />

      <div className="py-16 bg-slate-50 min-h-screen">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* LATEST NEWS & GOVERNANCE UPDATE */}
          <div className="mb-16">
            <div className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-pacieh-blue to-pacieh-green text-white py-4 px-6 sm:px-8 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MegaphoneIcon className="h-6 w-6 text-emerald-300" />
                  <h2 className="text-xl sm:text-2xl font-bold font-display">Special Announcements & Events</h2>
                </div>
                <span className="text-xs font-semibold bg-white/20 px-3 py-1 rounded-full uppercase tracking-wider">
                  Official Bulletin
                </span>
              </div>

              <div className="p-6 sm:p-10 space-y-12">
                {/* Leadership Update */}
                <div className="border-b border-gray-100 pb-10">
                  <div className="inline-flex items-center gap-2 bg-pacieh-green/10 text-pacieh-green px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                    <UserGroupIcon className="h-4 w-4" />
                    Leadership & Governance Milestone
                  </div>

                  <h3 className="text-2xl font-bold text-pacieh-blue font-display mb-4">
                    Executive Leadership Appointment
                  </h3>

                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 sm:p-8">
                    <p className="text-base sm:text-lg text-gray-800 leading-relaxed mb-6">
                      <strong className="text-pacieh-blue font-bold">Professor Chinyelu Angela Ekwunife</strong> has been appointed as the new <strong className="text-pacieh-green">Chief Executive Officer (CEO)</strong> of PACIEH. Founder <strong className="text-pacieh-blue">Professor Uche Amazigo FAS</strong> continues to provide strategic leadership and oversight as <strong className="text-pacieh-green">Chairman of the Board of Trustees</strong>.
                    </p>

                    <h4 className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-3">
                      Current Board of Trustees (BOT):
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      {[
                        'Prof. Uche Amazigo FAS (Chairman)',
                        'Dr. Ngozi Njepuome',
                        'Prof. Chinyelu A. Ekwunife (CEO)',
                        'Engr. Okwuchukwu Emejulu',
                        'Dr. Forster Ihejiofor',
                        'Rev. Fr. Ambrose Ekeroku',
                      ].map((name) => (
                        <div key={name} className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-xl border border-gray-200 text-xs font-semibold text-gray-800">
                          <CheckCircleIcon className="h-4 w-4 text-pacieh-green shrink-0" />
                          <span>{name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Kigali International Conference Feature */}
                <div className="border-b border-gray-100 pb-12">
                  <div className="inline-flex items-center gap-2 bg-[#EDF4ED] text-[#0B3B24] border border-[#0B3B24]/20 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                    <GlobeAltIcon className="h-4 w-4 text-[#165a39]" />
                    Global Health Mission & International Diplomacy
                  </div>

                  <h3 className="text-2xl sm:text-3xl font-black text-gray-900 font-display mb-3">
                    PACIEH Represented at 17th Annual NTD NGO Network Conference in Kigali, Rwanda
                  </h3>

                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-6">
                    <span className="inline-flex items-center gap-1">
                      <MapPinIcon className="h-3.5 w-3.5 text-pacieh-green" />
                      Marriott Hotel, Kigali, Rwanda
                    </span>
                    <span>•</span>
                    <span className="inline-flex items-center gap-1">
                      <CalendarDaysIcon className="h-3.5 w-3.5 text-pacieh-green" />
                      29 September – 1 October 2026
                    </span>
                    <span>•</span>
                    <span className="bg-emerald-50 text-emerald-800 font-semibold px-2 py-0.5 rounded">
                      Official Mission Report Available
                    </span>
                  </div>

                  <div className="grid md:grid-cols-12 gap-8 items-center bg-[#F8FAF8] rounded-2xl p-6 sm:p-8 border border-gray-200">
                    <div className="md:col-span-7 space-y-4">
                      <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                        PACIEH Chief Executive Officer <strong className="text-gray-900 font-bold">Prof. Chinyelu Angela Ekwunife</strong> led the organization’s delegation to the 17th Annual NTD NGO Network (NNN) Conference. The international summit convened global leaders under the framework of the WHO 2030 NTD Roadmap, focusing on multi-sectoral actions linking disease elimination with school nutrition, community WASH facilities, and primary healthcare delivery.
                      </p>

                      <div className="space-y-2 bg-white rounded-xl p-4 border border-gray-200/80 text-xs text-gray-700">
                        <div className="font-bold text-gray-900 flex items-center gap-1.5">
                          <SparklesIcon className="h-3.5 w-3.5 text-pacieh-green" />
                          Key Delegations & Bilateral Strategic Agreements:
                        </div>
                        <ul className="space-y-1 text-gray-600 pl-4 list-disc">
                          <li><strong>MITOSATH:</strong> Strategic collaboration discussions with Executive Director <em>Dr. Francisca Olamiju</em> on grassroots deworming distribution.</li>
                          <li><strong>DAHW Germany:</strong> Partnership framework with Medical Advisor <em>Dr. Anil Fastenau</em> on tuberculosis, leprosy, and vulnerable health interventions.</li>
                          <li><strong>University of Kigali:</strong> Academic research alliance with DVC <em>Prof. Ogechi Adeola</em> on joint health systems research.</li>
                          <li><strong>PPSN & Wigwe University:</strong> Coordinated policy engagement with <em>Prof. Chinyere Ukaga</em> and public health fellows.</li>
                        </ul>
                      </div>

                      <div className="flex flex-wrap items-center gap-3 pt-2">
                        <a
                          href="/reports/Kigali-Report-2026.docx"
                          download="PACIEH-Kigali-Report-2026.docx"
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0B3B24] text-white hover:bg-[#165a39] text-xs font-bold shadow-sm transition-all"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4 text-[#8de058]" />
                          Download Kigali Report (DOCX • 24.2 MB)
                        </a>
                        <Link
                          to="/reports"
                          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 text-xs font-semibold transition-all"
                        >
                          View in Reports Page
                        </Link>
                        <Link
                          to="/gallery"
                          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 text-xs font-semibold transition-all"
                        >
                          <PhotoIcon className="h-4 w-4 text-pacieh-green" />
                          View Photos in Gallery
                        </Link>
                      </div>
                    </div>

                    <div className="md:col-span-5">
                      <div className="rounded-2xl overflow-hidden shadow-md border-2 border-white bg-white group">
                        <div className="relative aspect-[4/3]">
                          <img
                            src="/images/KIGALI CONFERENCE/kigali-opening-ceremony.jpeg"
                            alt="17th Annual NNN Conference Kigali Opening Ceremony"
                            className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                          <div className="absolute bottom-3 left-3 right-3 text-white">
                            <span className="text-[10px] font-extrabold uppercase tracking-wider bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded text-[#8de058]">
                              Kigali Marriott Hotel
                            </span>
                            <p className="text-xs font-medium text-white/90 mt-1 line-clamp-1">
                              Plenary Opening Session of the 17th Annual NNN Conference
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Webinar Section */}
                <div className="grid md:grid-cols-2 gap-10 items-center">
                  <div className="space-y-5">
                    <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-900 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
                      <VideoCameraIcon className="h-4 w-4" />
                      Global Health Webinar Series
                    </div>

                    <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 font-display leading-tight">
                      Collaborative Pathways for Health: Linking One Health, Research & Community Implementation
                    </h3>

                    <div className="space-y-3 bg-white p-5 rounded-2xl border border-gray-200 text-sm text-gray-700">
                      <div className="flex items-center gap-3">
                        <CalendarDaysIcon className="h-5 w-5 text-pacieh-green shrink-0" />
                        <span className="font-semibold text-gray-900">Date:</span>
                        <span>Wednesday, November 19, 2025</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <ClockIcon className="h-5 w-5 text-pacieh-green shrink-0" />
                        <span className="font-semibold text-gray-900">Time:</span>
                        <span>10:00 AM (UTC)</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="font-semibold text-gray-900 min-w-[70px]">Join Link:</span>
                        <a 
                          href="https://bit.ly/tdrglobalafricanov" 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-pacieh-green font-semibold hover:underline break-all inline-flex items-center gap-1"
                        >
                          bit.ly/tdrglobalafricanov
                          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 shrink-0" />
                        </a>
                      </div>
                      <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
                        <span>Meeting ID: <strong>882 5122 0605</strong> • Passcode: <strong>282096</strong></span>
                      </div>
                    </div>

                    <div className="bg-pacieh-light p-5 rounded-2xl border border-pacieh-blue/10">
                      <p className="text-xs font-bold uppercase tracking-wider text-pacieh-blue mb-2">Featured Speakers & Chair:</p>
                      <ul className="space-y-1.5 text-xs text-gray-700">
                        <li>• <strong>Prof. Chinyelu Angela Ekwunife</strong> (Nnamdi Azikiwe University & PACIEH CEO)</li>
                        <li>• <strong>Dr. Ndong Ignatius Cheng</strong> (WANETAM Research Fellow)</li>
                        <li className="pt-1 text-gray-500">• Moderator: <strong>Prof. Emmanuel Asampong</strong> (University of Ghana)</li>
                      </ul>
                    </div>
                  </div>

                  <div className="relative">
                    <div className="rounded-2xl overflow-hidden shadow-xl border border-gray-200 bg-white">
                      <img
                        src={webinarImage}
                        alt="Collaborative Pathways for Health Webinar"
                        className="w-full h-auto object-cover hover:scale-102 transition-transform duration-300"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Press Coverage Grid */}
          <div>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <span className="text-xs font-bold uppercase tracking-wider text-pacieh-green">
                In The Spotlight
              </span>
              <h2 className="text-3xl font-extrabold text-pacieh-blue font-display mt-1">
                Major Press & Media Features
              </h2>
              <p className="text-sm text-gray-600 mt-2">
                Discover independent coverage and investigative reporting on PACIEH's field breakthroughs.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {newsItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  className="bg-white rounded-2xl border border-gray-200 p-6 flex flex-col justify-between hover:shadow-xl hover:border-pacieh-green/40 transition-all group"
                >
                  <div>
                    <div className="flex items-center justify-between text-xs mb-3">
                      <span className="font-bold text-pacieh-green bg-pacieh-green/10 px-3 py-1 rounded-full uppercase tracking-wider text-[11px]">
                        {item.source}
                      </span>
                      <span className="text-gray-400 font-medium">{item.date}</span>
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-pacieh-blue transition-colors mb-3 leading-snug">
                      {item.title}
                    </h3>

                    <p className="text-sm text-gray-600 leading-relaxed mb-6 line-clamp-3">
                      {item.excerpt}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-gray-100 flex items-center justify-end">
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-pacieh-blue group-hover:text-pacieh-green transition-colors"
                    >
                      Read Full Article
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </>
  )
}